import { PortalClassesEnum } from "@/ts/enums/PortalClassesEnum";
import { getTrackingId } from "@/utils/utils";
import { faBars, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    Flex,
    MenuContent,
    MenuItem,
    MenuPositioner,
    MenuRoot,
    MenuTrigger,
    Portal
} from "@chakra-ui/react";

import { isActiveUrl } from "@utils/utils";

export interface NavItem {
    id: string | number;
    name: string;
    route: string;
    isExternal?: boolean;
}

interface MobileNavProps {
    navItems: NavItem[];
    userDisplayName?: string;
    profileMenuItems?: React.ReactNode;
    showProfile?: boolean;
}

const MobileNav = ({
    navItems,
    userDisplayName,
    profileMenuItems,
    showProfile = false
}: MobileNavProps) => {
    const handleNavigation = (route: string, isExternal?: boolean) => {
        if (isExternal) {
            window.open(route, "_blank", "noopener noreferrer");
        } else {
            window.location.href = route;
        }
    };

    return (
        <MenuRoot>
            <MenuTrigger
                cursor="pointer"
                color="gray.700"
                _focus={{
                    outline: "none",
                    boxShadow: "none"
                }}
                _focusVisible={{
                    outline: "none",
                    boxShadow: "none"
                }}
                _dark={{
                    color: "gray.300"
                }}
            >
                <FontAwesomeIcon
                    icon={faBars}
                    fontSize={24}
                    data-testid="barImg"
                />
            </MenuTrigger>
            <Portal>
                <MenuPositioner>
                    <MenuContent
                        minW="220px"
                        maxH="80vh"
                        overflowY="auto"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="8px"
                        boxShadow="lg"
                        p="0.5rem"
                        _dark={{
                            bg: "base.800",
                            borderColor: "base.600"
                        }}
                    >
                        {navItems.map(item => {
                            const isActive =
                                item.route && !item.isExternal
                                    ? isActiveUrl(item.route)
                                    : false;
                            return (
                                <MenuItem
                                    key={item.id}
                                    value={item.route}
                                    onClick={() =>
                                        handleNavigation(
                                            item.route,
                                            item.isExternal
                                        )
                                    }
                                    padding="0.5rem 0.75rem"
                                    fontSize="1rem"
                                    fontWeight="500"
                                    color={isActive ? "base.800" : "gray.600"}
                                    bg={isActive ? "base.50" : "transparent"}
                                    borderRadius="6px"
                                    margin="2px 0"
                                    transition="all 0.2s ease"
                                    position="relative"
                                    _hover={{
                                        bg: "base.100",
                                        color: "base.800",
                                        _dark: {
                                            bg: "base.700",
                                            color: "gray.200"
                                        }
                                    }}
                                    _active={{
                                        color: "base.800",
                                        _dark: { color: "gray.200" }
                                    }}
                                    _dark={{
                                        color: isActive
                                            ? "gray.200"
                                            : "gray.400",
                                        bg: isActive
                                            ? "base.600"
                                            : "transparent"
                                    }}
                                    data-tracking-id={getTrackingId(
                                        [PortalClassesEnum.Link],
                                        item.name
                                    )}
                                    _before={
                                        isActive
                                            ? {
                                                  content: "''",
                                                  position: "absolute",
                                                  left: "0.25rem",
                                                  top: "50%",
                                                  transform: "translateY(-50%)",
                                                  width: "3px",
                                                  height: "1rem",
                                                  bg: "burntYellow.500",
                                                  borderRadius: "2px"
                                              }
                                            : {}
                                    }
                                >
                                    <Flex
                                        display="inline-flex"
                                        alignItems="center"
                                        gap="0.5rem"
                                        ml={isActive ? "0.5rem" : "0"}
                                    >
                                        {item.name}
                                        {item.isExternal && (
                                            <FontAwesomeIcon
                                                icon={faUpRightFromSquare}
                                                width="10px"
                                                height="10px"
                                                data-testid="externalImg"
                                            />
                                        )}
                                    </Flex>
                                </MenuItem>
                            );
                        })}

                        {showProfile && userDisplayName && (
                            <>
                                {/* <MenuSeparator
                                    margin="0.5rem 0"
                                    borderColor="gray.200"
                                    _dark={{ borderColor: "base.600" }}
                                /> */}
                                {profileMenuItems}
                            </>
                        )}
                    </MenuContent>
                </MenuPositioner>
            </Portal>
        </MenuRoot>
    );
};

export default MobileNav;
